//node_modules
import React from "react";
import { NextPage } from "next";
import NextLink from "@adjust/website/src/components/NextLink";
import NextImage from "next/legacy/image";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { css } from "@adjust/libs/emotion";

//libs
import getBootstrapSizes from "@adjust/website/libs/helper/getBoostrapSizes";

//layout
import SiteLayout from "src/layouts/SiteLayout";

//components
import StaticBackgroundComponent from "@adjust/website/src/components/StaticBackgroundComponent";
import ParallaxHeaderComponent from "@adjust/website/src/components/ParallaxHeaderComponent";

//locals
import MetaComponent from "./components/MetaDataComponent";

//styles
const imageContainer = css({
  position: "relative",
  width: "100%",
  margin: "auto",
  maxWidth: "320px",

  "> span": {
    borderRadius: "25px",
  },
});

const phoneCSS = css({
  textAlign: "center",
  padding: "8px 0",

  "> a": {
    fontSize: "1.5em",
    textDecoration: "none",
  },
});

const headerCSS = css({
  fontFamily: '"lato", sans-serif',
  fontSize: "3em",
  textAlign: "center",
});

const titleCSS = css({
  fontFamily: '"lato", sans-serif',
  fontSize: "1.25em",
  textAlign: "center",
});

const blockCSS = css({
  fontSize: "1.15em",
  marginTop: "1.5rem",
});

//images
import stacy from "@adjust/public/images/9e528c4d2bebc8f4e28a959be207953c.png";
import haley from "@adjust/public/images/f7091e063ae7ef9ef00e1d938eb4587a.png";
import mike from "@adjust/public/images/54906fd6f6167f99389b7214c3c360ce.png";
import jacob from "@adjust/public/images/012bdf8fbbf24598cebe4763b1ed65ab.png";
import parallax1 from "@adjust/public/images/d40aaf23ea6975f9fd137914e53aeaef.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons";

const sizes = getBootstrapSizes({ xs: 256 });

const AboutPage: NextPage = () => {
  return (
    <SiteLayout>
      <MetaComponent />
      <StaticBackgroundComponent bgImage={parallax1} bgImageAlt="Parallax Background Image" quality={75} fetchPriority="high" priority>
        <ParallaxHeaderComponent>Learn About Us!</ParallaxHeaderComponent>
      </StaticBackgroundComponent>
      <Container className="my-2">
        {/* STACY GRAY */}
        <Row className="my-5">
          <Col lg={4} className="mt-lg-5 mb-auto text-center">
            <Card.Body className={imageContainer}>
              <NextImage src={stacy} alt="Image of Stacy Gray" sizes={sizes} quality={75} placeholder="blur" objectFit="contain" />
            </Card.Body>
          </Col>

          <Col lg={8}>
            <h2 className={headerCSS}>Stacy Gray</h2>
            <h3 className={titleCSS}>Owner | Luxury Travel Advisor | Elite Cruise Counselor (ECC)</h3>

            <div className={phoneCSS}>
              <NextLink href="tel:+1-919-810-3743">
                <Button variant="primary" as="a">
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  +1 919-810-3743
                </Button>
              </NextLink>
            </div>

            <Card.Text className={blockCSS}>
              Travel has the power to transform lives, create unforgettable memories, and bring families closer together. That's why I founded{" "}
              <b>Adjust to the Good Life Travel, LLC</b> in 2013—to help my clients experience the world with confidence while creating extraordinary journeys tailored specifically
              to them. I believe exceptional travel planning begins with firsthand experience, continuous education, and trusted industry relationships. Every itinerary is
              thoughtfully designed around your travel style, ensuring every detail is handled so you can simply enjoy the journey.
            </Card.Text>

            <Card.Text className={blockCSS}>
              Today, I specialize in luxury ocean, river, expedition, and yacht cruises, as well as custom-designed international vacations throughout Europe, Australia, Asia,
              Antarctica, the Arctic, and beyond. Whether you're celebrating a honeymoon, planning a once-in-a-lifetime expedition, organizing a multigenerational family vacation,
              or embarking on the luxury cruise you've always dreamed about, I personally guide every step of your journey.
            </Card.Text>

            <Card.Text className={blockCSS}>
              What truly sets me apart is the depth of my firsthand experience. I've personally sailed on <b>more than 50 cruises across 13 different cruise lines</b> and explored{" "}
              <b>nearly 50 countries</b>, allowing me to provide recommendations based on personal knowledge rather than brochures alone. My dedication to luxury travel has also
              resulted in <b>more than $1 million in luxury cruise sales</b> and selection to the <b>Travel Leaders Alliance Luxury Leaders Alliance</b> for three consecutive
              years—an exclusive recognition reserved for the <b>top 1% of travel advisors in the United States</b>.
            </Card.Text>

            <Card.Text className={blockCSS}>
              I believe my clients deserve advice backed by education, experience, and continuous professional development. Over the years, I've advanced through CLIA's
              professional designation program from Accredited Cruise Counselor to Master Cruise Counselor, ultimately earning the prestigious <b>Elite Cruise Counselor (ECC)</b>{" "}
              designation. I've also completed specialty certifications in <b>Luxury Cruising, River Cruising,</b> and <b>Accessible Travel</b>, allowing me to confidently match
              each client with the cruise line, destination, itinerary, or resort that best fits their travel style and expectations.
            </Card.Text>

            <Card.Text className={blockCSS}>
              Before becoming a full-time luxury travel advisor, I owned and operated a successful chiropractic practice in Raleigh, North Carolina, for nearly 30 years. That
              experience shaped the way I serve my clients today—with honesty, attention to detail, personalized care, and long-lasting relationships built on trust. For me,
              planning travel isn't simply about booking a vacation—it's about understanding what inspires you and creating an experience you'll remember for the rest of your life.
              Whether you're dreaming of sailing through the Norwegian fjords, exploring Antarctica aboard an expedition ship, cruising Europe's rivers, relaxing at a luxury
              all-inclusive resort, or embarking on a custom-designed journey across the globe, I would be honored to help you explore the world—one incredible journey at a time.
            </Card.Text>
          </Col>
        </Row>
        {/* MIKE GRAY */}
        <Row className="my-5">
          <Col lg={{ span: 4, order: "last" }} className="mt-lg-5 mb-auto text-center">
            <Card.Body className={imageContainer}>
              <NextImage src={mike} alt="Image of Mike Gray" sizes={sizes} quality={75} placeholder="blur" objectFit="contain" />
            </Card.Body>
          </Col>
          <Col lg={8}>
            <h2 className={headerCSS}>Michael Gray</h2>
            <h3 className={titleCSS}>Cruise Specialist</h3>
            <div className={phoneCSS}>
              <NextLink href="tel:+1-919-710-7461">
                <Button variant="primary" as="a">
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  +1 919-710-7461
                </Button>
              </NextLink>
            </div>
            <Card.Text className={blockCSS}>
              Michael Gray joined his wife, Stacy Gray, at Adjust to the Good Life Travel as a travel advisor in 2025, bringing his passion for cruising to the heart of the agency
              as one of our dedicated Cruise Specialists. With more than 40 cruises sailed across 10+ different cruise lines, Michael offers clients something invaluable — real,
              firsthand experience. He has explored everything from Caribbean sailings and European river cruises to unforgettable Disney voyages, giving him deep insight into the
              unique personality, service style, and onboard experience each cruise line offers.
            </Card.Text>
            <Card.Text className={blockCSS}>
              River cruising is his personal favorite, especially sailing past Europe’s castles and storybook villages. However, his expertise spans ocean cruising, luxury lines,
              and premium and family-focused brands.
            </Card.Text>
            <Card.Text className={blockCSS}>
              Michael is passionate about helping clients navigate the many options within the cruise world. His goal is simple: to deliver a seamless, personalized voyage that
              exceeds expectations, maximizes value, and is perfectly curated from start to finish.
            </Card.Text>
          </Col>
        </Row>
        {/* JACOB GRAY */}
        <Row className="my-5">
          <Col lg={4} className="mt-lg-5 mb-auto text-center">
            <Card.Body className={imageContainer}>
              <NextImage src={jacob} alt="Image of Jacob Gray" sizes={sizes} quality={75} placeholder="blur" objectFit="contain" />
            </Card.Body>
          </Col>
          <Col lg={8}>
            <h2 className={headerCSS}>Jacob Gray</h2>
            <h3 className={titleCSS}>Guided Tour Travel Specialist</h3>
            <div className={phoneCSS}>
              <NextLink href="tel:+1-919-710-0295">
                <Button variant="primary" as="a">
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  +1 919-710-0295
                </Button>
              </NextLink>
            </div>
            <Card.Text className={blockCSS}>
              Jacob joined Adjust to the Good Life Travel as a travel advisor in 2025 bringing a unique blend of numbers-minded precision and a genuine love for travel. With an
              accounting background, he naturally spots the details others miss — keeping your trip organized, optimized, and aligned with your budget from start to finish.
            </Card.Text>
            <Card.Text className={blockCSS}>
              He specializes in expertly curated guided tours that allow you to experience the world with ease, insight, and confidence. From Europe’s historic cities to
              breathtaking natural wonders, he partners with trusted tour operators to ensure seamless logistics, immersive cultural experiences, and exceptional value — so you can
              simply enjoy the journey.
            </Card.Text>
            <Card.Text className={blockCSS}>
              When it comes to guided travel, he loves working with trusted partners like Collette, Globus, Tauck and Gate 1 Travel — companies known for immersive experiences and
              well-crafted itineraries.
            </Card.Text>
            <Card.Text className={blockCSS}>
              Jacob believes great travel planning is about balance: maximizing experiences while keeping everything financially sound and thoughtfully structured. He matches you
              with the right itinerary and tour company to fit your travel style and budget. He helps clients experience bucket-list destinations without the overwhelm of planning.
              From transportation and accommodations to exclusive excursions, every detail is handled. If you want a trip that’s carefully planned, budget-conscious, and built for
              real memories — Jacob is ready to help you get there!
            </Card.Text>
          </Col>
        </Row>
        {/* HALEY GRAY */}
        <Row className="my-5">
          <Col lg={{ span: 4, order: "last" }} className="mt-lg-5 mb-auto text-center">
            <Card.Body className={imageContainer}>
              <NextImage src={haley} alt="Image of Haley Gray" sizes={sizes} quality={75} placeholder="blur" objectFit="contain" />
            </Card.Body>
          </Col>
          <Col lg={{ span: 8, order: "first" }}>
            <Card.Body>
              <h2 className={headerCSS}>Haley Gray</h2>
              <h3 className={titleCSS}>Travel Advisor</h3>
              <div className={phoneCSS}>
                <NextLink href="tel:+1-919-710-2657">
                  <Button variant="primary" as="a">
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    +1 919-710-2657
                  </Button>
                </NextLink>
              </div>
              <Card.Text className={blockCSS}>
                Adventure has always been at the heart of Haley’s story. Joining Adjust to the Good Life Travel alongside her mom, Stacy Gray, was a natural next step — an
                extension of a life shaped by exploration and meaningful connection through travel.
              </Card.Text>
              <Card.Text className={blockCSS}>
                Having visited 41 states and 50 countries growing up, Haley developed a global perspective early on. Those experiences sparked a deep love for culture, new
                destinations, and the personal growth that happens when you step beyond the familiar. Whether she’s skydiving, paragliding, or spotting wildlife in the wild, Haley
                is always ready for the next adventure.
              </Card.Text>
              <Card.Text className={blockCSS}>
                Today, she specializes in luxury all-inclusive vacations and cruises designed to feel seamless, personalized, and unforgettable. She is passionate about creating
                meaningful travel experiences — from relaxed, family-friendly beach retreats to high-energy college spring break groups and expertly curated cruise itineraries that
                make exploring multiple destinations effortless. Haley focuses on every detail so her clients can simply relax and enjoy the journey.
              </Card.Text>
              <Card.Text className={blockCSS}>
                With firsthand travel experience, thoughtful planning, and genuine enthusiasm, Haley turns dream vacations into lasting memories.
              </Card.Text>
              <Card.Text className={blockCSS}>Ready to explore with purpose? Because travel isn’t just about the destination — it’s about who you become along the way.</Card.Text>
            </Card.Body>
          </Col>
        </Row>
      </Container>
    </SiteLayout>
  );
};

export default AboutPage;
